import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ArrowLeft, Loader2, Save, FileDown, Star, X, Camera, Plus, Trash2,
  Share2, Copy, Globe, Lock, TrendingUp, ExternalLink,
} from 'lucide-react';
import { API } from '../../../lib/api';
import { useApiFeedback, formatApiError } from '../../../lib/useApiFeedback';

// ---------------------------------------------------------------------------
// Traffic-light color for a status pill (green = first choice, red = last).
// Mirrors the backend `_status_color` scoring semantics so the form and the
// PDF look consistent.
// ---------------------------------------------------------------------------
const statusChipClasses = (status, choices) => {
  if (!status || !choices || choices.length === 0) {
    return 'bg-slate-500/10 text-slate-300 border-slate-500/30';
  }
  if (status === 'N/A') return 'bg-slate-500/10 text-slate-300 border-slate-500/30';
  const nonNa = choices.filter((c) => c !== 'N/A');
  const idx = nonNa.indexOf(status);
  if (idx < 0) return 'bg-slate-500/10 text-slate-300 border-slate-500/30';
  const ratio = nonNa.length <= 1 ? 0 : idx / (nonNa.length - 1);
  if (ratio <= 0.34) return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40';
  if (ratio <= 0.67) return 'bg-amber-500/15 text-amber-300 border-amber-500/40';
  return 'bg-red-500/15 text-red-300 border-red-500/40';
};

const StarPicker = ({ value, onChange }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? null : n)}
        className="p-0.5 hover:scale-110 transition"
        aria-label={`Rate ${n} star`}
      >
        <Star
          size={14}
          className={value && n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-500'}
        />
      </button>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Parameter row — status pill, stars (optional), notes, photo thumbnails.
// ---------------------------------------------------------------------------
const ParamRow = ({ item, result, onChange, onUploadPhoto, onRemovePhoto, uploadingKey }) => {
  const { key, label, choices } = item;
  const status = result?.status || '';
  const stars = result?.stars || null;
  const notes = result?.notes || '';
  const value = result?.value || '';
  const photos = result?.photos || [];
  const isNumeric = !choices; // schema has choices=null for numeric-only params

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 py-3 border-b border-white/5 last:border-0" data-testid={`param-row-${key}`}>
      <div className="md:col-span-4 text-sm text-slate-200 flex items-center">
        {label}
      </div>

      {/* Status / numeric input */}
      <div className="md:col-span-3">
        {isNumeric ? (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(key, { value: e.target.value })}
            placeholder="Value"
            className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-teal-400"
            data-testid={`param-value-${key}`}
          />
        ) : (
          <select
            value={status}
            onChange={(e) => onChange(key, { status: e.target.value })}
            className={`w-full text-xs rounded px-2 py-1.5 border focus:outline-none focus:ring-1 focus:ring-teal-400 ${statusChipClasses(status, choices)}`}
            data-testid={`param-status-${key}`}
          >
            <option value="" className="bg-slate-800 text-slate-300">— Select —</option>
            {choices.map((c) => (
              <option key={c} value={c} className="bg-slate-800 text-slate-200">{c}</option>
            ))}
          </select>
        )}
      </div>

      {/* Stars */}
      <div className="md:col-span-2 flex items-center">
        <StarPicker value={stars} onChange={(v) => onChange(key, { stars: v })} />
      </div>

      {/* Notes */}
      <div className="md:col-span-3">
        <input
          type="text"
          value={notes}
          onChange={(e) => onChange(key, { notes: e.target.value })}
          placeholder="Notes"
          className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-teal-400"
          data-testid={`param-notes-${key}`}
        />
      </div>

      {/* Photos row (spans full width) */}
      <div className="md:col-span-12 flex flex-wrap items-center gap-2 mt-1">
        {photos.map((url) => (
          <div key={url} className="relative w-14 h-14 rounded overflow-hidden border border-white/10 group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onRemovePhoto(key, url)}
              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition"
              aria-label="Remove photo"
            >
              <X size={14} className="text-white" />
            </button>
          </div>
        ))}
        <label className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-teal-300 cursor-pointer border border-dashed border-white/20 rounded px-2 py-1 transition">
          {uploadingKey === key ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Camera size={12} />
          )}
          <span>Photo</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUploadPhoto(key, f);
              e.target.value = '';
            }}
            data-testid={`param-photo-${key}`}
          />
        </label>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Repair estimate line item editor
// ---------------------------------------------------------------------------
const RepairEstimateEditor = ({ items, onChange }) => {
  const addLine = () => onChange([...(items || []), { category: 'Exterior', part: '', type: 'Repair', price: 0 }]);
  const updateLine = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const removeLine = (i) => onChange(items.filter((_, j) => j !== i));

  const grandTotal = (items || []).reduce((sum, x) => sum + (Number(x.price) || 0), 0);

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-200">Repair Estimate</h3>
        <button
          type="button"
          onClick={addLine}
          className="inline-flex items-center gap-1 text-xs bg-teal-500/15 hover:bg-teal-500/25 border border-teal-500/30 text-teal-300 px-2 py-1 rounded"
          data-testid="add-repair-line"
        >
          <Plus size={12} /> Add line
        </button>
      </div>
      {(items || []).length === 0 && (
        <p className="text-xs text-slate-500 py-3">No repair estimates yet.</p>
      )}
      {(items || []).map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`repair-line-${i}`}>
          <select
            value={it.category || 'Exterior'}
            onChange={(e) => updateLine(i, { category: e.target.value })}
            className="col-span-3 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-teal-400"
          >
            {['Exterior', 'Interior', 'Engine', 'Tyres', 'Electricals', 'Other'].map((c) => (
              <option key={c} value={c} className="bg-slate-800">{c}</option>
            ))}
          </select>
          <input
            type="text"
            value={it.part || ''}
            onChange={(e) => updateLine(i, { part: e.target.value })}
            placeholder="Part / area"
            className="col-span-4 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-teal-400"
          />
          <select
            value={it.type || 'Repair'}
            onChange={(e) => updateLine(i, { type: e.target.value })}
            className="col-span-2 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-teal-400"
          >
            {['Repair', 'Replacement', 'Repaint', 'Realignment'].map((c) => (
              <option key={c} value={c} className="bg-slate-800">{c}</option>
            ))}
          </select>
          <input
            type="number"
            value={it.price ?? 0}
            onChange={(e) => updateLine(i, { price: Number(e.target.value) || 0 })}
            placeholder="Price ₹"
            className="col-span-2 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-teal-400 text-right"
          />
          <button
            type="button"
            onClick={() => removeLine(i)}
            className="col-span-1 flex items-center justify-center text-slate-400 hover:text-red-300"
            aria-label="Remove line"
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}
      {(items || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-sm">
          <span className="text-slate-400">Grand Total</span>
          <span className="font-semibold text-teal-300" data-testid="repair-grand-total">₹ {grandTotal.toLocaleString('en-IN')}</span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------
const UsedCarInspectionForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { authHeader } = useAuth();

  const [schema, setSchema] = useState([]);
  const [form, setForm] = useState({
    make: '', model: '', variant: '', manufacture_year: '', km_driven: '',
    fuel_type: '', transmission: '', body_type: '', color: '',
    engine_cc: '', seat_capacity: '', emission_norms: '',
    registration_number: '', registration_date: '', registration_expiry: '',
    rto_authority: '', chassis_number: '', engine_number: '',
    insurance_valid_until: '', puc_valid_until: '',
    owner_name: '', owner_phone: '', ownership_type: 'Individual', owner_count: 1,
    booking_id: '', inspection_date: new Date().toISOString().slice(0, 10),
    inspection_location: '', inspector_name: '',
    results: {}, repair_estimates: [],
    valuation_min: '', valuation_max: '', valuation_notes: '',
    market_comparables: [],
    outstanding_fines_notes: '',
    rc_verification: null,
    service_history_records: [],
    overall_notes: '', status: 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedId, setSavedId] = useState(id || null);
  const [computed, setComputed] = useState({ overall_score: null, overall_grade: null, section_scores: {} });
  const [uploadingKey, setUploadingKey] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [share, setShare] = useState({ is_public: false, share_token: '' });
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [vehicleCatalog, setVehicleCatalog] = useState({ cars: {}, car_variants: {}, _generic_variants: [] });
  const [valuationBusy, setValuationBusy] = useState(false);
  const [valuationResult, setValuationResult] = useState(null); // { comparables, sources_status, fallback_urls, message, ... }
  const firstLoad = useRef(true);

  // Fetch curated Make → Model → Variant catalog once. Sourced from
  // /api/billing/vehicles/catalog (curated JSON — CarWale + CarDekho lineups)
  // plus admin-added catalog overrides.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/vehicles/catalog`, { headers: authHeader() });
        setVehicleCatalog({
          cars: data.cars || {},
          car_variants: data.car_variants || {},
          _generic_variants: data._generic_variants || [],
        });
      } catch {
        // Non-fatal — the fields still accept free-text without suggestions
      }
    })();
  }, [authHeader]);

  // Fetch schema + existing inspection (if editing) on mount
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: schemaData }, existing] = await Promise.all([
          axios.get(`${API}/used-car-inspections/schema`, { headers: authHeader() }),
          isEdit ? axios.get(`${API}/used-car-inspections/${id}`, { headers: authHeader() }) : Promise.resolve(null),
        ]);
        setSchema(schemaData.sections || []);
        // Default expanded = first section
        setExpandedSection((schemaData.sections || [])[0]?.slug || null);
        if (existing?.data) {
          const d = existing.data;
          setForm((f) => ({
            ...f,
            ...d,
            manufacture_year: d.manufacture_year ?? '',
            km_driven: d.km_driven ?? '',
            engine_cc: d.engine_cc ?? '',
            seat_capacity: d.seat_capacity ?? '',
            valuation_min: d.valuation_min ?? '',
            valuation_max: d.valuation_max ?? '',
            owner_count: d.owner_count ?? 1,
            results: d.results || {},
            repair_estimates: d.repair_estimates || [],
            market_comparables: d.market_comparables || [],
            outstanding_fines_notes: d.outstanding_fines_notes || '',
            rc_verification: d.rc_verification || null,
            service_history_records: d.service_history_records || [],
          }));
          setSavedId(d.id);
          setComputed({
            overall_score: d.overall_score, overall_grade: d.overall_grade,
            section_scores: d.section_scores || {},
          });
          setShare({
            is_public: Boolean(d.is_public),
            share_token: d.share_token || '',
          });
          // Restore the comparables preview when re-opening an inspection so
          // the inspector sees exactly what will land in the PDF appendix.
          if ((d.market_comparables || []).length > 0) {
            setValuationResult({
              status: 'ok',
              comparables: d.market_comparables,
              valuation_min: d.valuation_min,
              valuation_max: d.valuation_max,
              fallback_urls: {},
              sources_status: {},
              message: `${d.market_comparables.length} comparable listings on record. The 5 nearest are printed as an appendix in the PDF.`,
            });
          }
        }
      } catch (e) {
        showError(e);
      } finally {
        setLoading(false);
        firstLoad.current = false;
      }
    })();
  }, [id]);

  // Update field helper
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Update a single parameter result (merges)
  const updateResult = useCallback((key, patch) => {
    setForm((f) => {
      const prev = f.results?.[key] || {};
      return { ...f, results: { ...(f.results || {}), [key]: { ...prev, ...patch } } };
    });
  }, []);

  // Client-side score preview (identical logic to backend, but light-weight)
  const previewScore = useMemo(() => {
    if (!schema.length) return { overall: 0, grade: 'Pending', sections: {} };
    const sectionScores = {};
    const allVals = [];
    for (const sec of schema) {
      const vals = [];
      for (const it of sec.items) {
        const r = form.results?.[it.key];
        const status = r?.status;
        const choices = it.choices;
        if (!status || !choices || status === 'N/A' || !choices.includes(status)) continue;
        const nonNa = choices.filter((c) => c !== 'N/A');
        const n = nonNa.length;
        if (n <= 1) { vals.push(1); continue; }
        vals.push(1 - (choices.indexOf(status) / (n - 1)));
      }
      if (vals.length) {
        sectionScores[sec.slug] = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 1000) / 10;
        allVals.push(...vals);
      }
    }
    const overall = allVals.length ? Math.round((allVals.reduce((a, b) => a + b, 0) / allVals.length) * 1000) / 10 : 0;
    let grade = 'Pending';
    if (allVals.length > 0) {
      if (overall >= 85) grade = 'Excellent';
      else if (overall >= 70) grade = 'Good';
      else if (overall >= 55) grade = 'Average';
      else grade = 'Poor';
    }
    return { overall, grade, sections: sectionScores };
  }, [schema, form.results]);

  const buildPayload = () => {
    const p = { ...form };
    // Coerce numeric fields
    ['manufacture_year', 'km_driven', 'engine_cc', 'seat_capacity', 'owner_count', 'valuation_min', 'valuation_max']
      .forEach((k) => {
        if (p[k] === '' || p[k] === null) p[k] = null;
        else p[k] = Number(p[k]);
      });
    return p;
  };

  const { error, showError, showSuccess, clearError, bannerRef: errorRef, setError } = useApiFeedback();

  const onSave = async ({ downloadAfter = false } = {}) => {
    setSaving(true);
    clearError();
    try {
      const payload = buildPayload();
      let doc;
      if (savedId) {
        const { data } = await axios.put(`${API}/used-car-inspections/${savedId}`, payload, { headers: authHeader() });
        doc = data;
      } else {
        const { data } = await axios.post(`${API}/used-car-inspections`, payload, { headers: authHeader() });
        doc = data;
        setSavedId(doc.id);
        window.history.replaceState(null, '', `/admin/used-car-inspections/${doc.id}`);
      }
      setComputed({
        overall_score: doc.overall_score, overall_grade: doc.overall_grade,
        section_scores: doc.section_scores || {},
      });
      if (doc.booking_id && !form.booking_id) setField('booking_id', doc.booking_id);
      showSuccess(downloadAfter ? 'Saved. Preparing PDF…' : 'Inspection saved.');
      if (downloadAfter) await onDownloadPdf(doc.id);
    } catch (e) {
      showError(e);
    } finally {
      setSaving(false);
    }
  };

  const onDownloadPdf = async (targetId) => {
    const idToUse = targetId || savedId;
    if (!idToUse) return;
    try {
      const res = await axios.get(`${API}/used-car-inspections/${idToUse}/pdf`, {
        headers: authHeader(), responseType: 'blob', timeout: 60000,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `UsedCarInspection_${form.booking_id || idToUse.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 800);
    } catch (err) {
      showError(err || 'Failed to download PDF');
    }
  };

  const onUploadPhoto = async (paramKey, file) => {
    if (!savedId) {
      // Save first so we have a real ID to attach photos to
      await onSave();
    }
    const currentId = savedId || form?.id;
    if (!currentId) return;
    setUploadingKey(paramKey);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('param_key', paramKey);
      const { data } = await axios.post(`${API}/used-car-inspections/${currentId}/photos`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      updateResult(paramKey, { photos: [...((form.results?.[paramKey]?.photos) || []), data.secure_url] });
    } catch (e) {
      showError(e);
    } finally {
      setUploadingKey(null);
    }
  };

  // ---------------------- Public sharing --------------------------------
  const publicUrl = share.share_token
    ? `${window.location.origin}/inspection/${share.share_token}`
    : '';

  const toggleShare = async () => {
    if (!savedId) {
      await onSave();
      if (!savedId) return;
    }
    setShareBusy(true);
    try {
      if (share.is_public) {
        await axios.delete(`${API}/used-car-inspections/${savedId}/share`, { headers: authHeader() });
        setShare((s) => ({ ...s, is_public: false }));
      } else {
        const { data } = await axios.post(`${API}/used-car-inspections/${savedId}/share`, {}, { headers: authHeader() });
        setShare({ is_public: true, share_token: data.share_token });
      }
    } catch (e) {
      showError(e);
    } finally {
      setShareBusy(false);
    }
  };

  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard permission denied — ignore */ }
  };

  // ---------------------- Market Valuation ------------------------------
  const fetchMarketValuation = async () => {
    if (!form.make || !form.model) {
      setError('Please fill Make and Model before fetching a market valuation.');
      return;
    }
    setValuationBusy(true);
    clearError();
    try {
      const { data } = await axios.post(
        `${API}/used-car-inspections/valuation-lookup`,
        {
          make: form.make,
          model: form.model,
          variant: form.variant || '',
          manufacture_year: form.manufacture_year ? Number(form.manufacture_year) : null,
          km_driven: form.km_driven ? Number(form.km_driven) : null,
          fuel_type: form.fuel_type || '',
          inspection_location: form.inspection_location || '',
        },
        { headers: authHeader() },
      );
      setValuationResult(data);
      // Auto-fill min/max AND persist the comparables into the form so the
      // next Save writes them to the DB — they're later printed as an
      // appendix in the generated PDF.
      if (data.comparables?.length) {
        setForm((f) => ({ ...f, market_comparables: data.comparables }));
      }
      if (data.valuation_min && data.valuation_max) {
        setForm((f) => ({
          ...f,
          valuation_min: data.valuation_min,
          valuation_max: data.valuation_max,
          valuation_notes: data.message
            || `Auto-computed from ${data.comparables.length} live comparable listings on Cars24, Spinny & Cartrade.`,
        }));
      }
    } catch (e) {
      showError(e);
    } finally {
      setValuationBusy(false);
    }
  };

  // ---------------------- Surepass fetch ---------------------------------
  const [surepassBusy, setSurepassBusy] = useState(false);
  const [surepassError, setSurepassError] = useState('');

  const fetchSurepass = async () => {
    if (!savedId) {
      showError('Save the inspection first, then fetch service history.');
      return;
    }
    if (!form.registration_number) {
      showError('Enter the vehicle registration number first.');
      return;
    }
    setSurepassBusy(true);
    setSurepassError('');
    try {
      const { data } = await axios.post(
        `${API}/used-car-inspections/${savedId}/fetch-service-history`,
        {},
        { headers: authHeader() },
      );
      setForm((f) => ({
        ...f,
        rc_verification: data.rc_verification || null,
        service_history_records: data.service_history?.records || [],
      }));
      if (data.service_history?.error) {
        setSurepassError(data.service_history.error);
      }
    } catch (e) {
      setSurepassError(e.response?.data?.detail || e.message);
    } finally {
      setSurepassBusy(false);
    }
  };

  const onRemovePhoto = async (paramKey, url) => {
    if (!savedId) return;
    try {
      await axios.delete(`${API}/used-car-inspections/${savedId}/photos`, {
        params: { param_key: paramKey, url }, headers: authHeader(),
      });
      updateResult(paramKey, {
        photos: (form.results?.[paramKey]?.photos || []).filter((p) => p !== url),
      });
    } catch (e) {
      showError(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400" data-testid="used-car-inspection-form-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }

  const gradeCardColor = {
    Excellent: 'bg-emerald-500',
    Good: 'bg-teal-500',
    Average: 'bg-amber-500',
    Poor: 'bg-red-500',
    Pending: 'bg-slate-500',
  }[computed.overall_grade || previewScore.grade];

  return (
    <div className="min-h-screen">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <button
            onClick={() => navigate('/admin/used-car-inspections')}
            className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-teal-300"
            data-testid="back-to-used-car-inspections"
          >
            <ArrowLeft size={16} /> Back to Used Car Inspections
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => onSave()}
              disabled={saving}
              className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-60"
              data-testid="save-used-car-inspection"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {savedId ? 'Save changes' : 'Save inspection'}
            </button>
            <button
              onClick={() => onSave({ downloadAfter: true })}
              disabled={saving}
              className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-sm px-4 py-2 rounded-lg disabled:opacity-60"
              data-testid="save-and-pdf-used-car-inspection"
            >
              <FileDown size={14} /> Save + PDF
            </button>
          </div>
        </div>

        {error && (
          <div
            ref={errorRef}
            className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
            data-testid="used-car-inspection-form-error"
            role="alert"
            aria-live="assertive"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="flex-1">{String(error)}</span>
              <button
                type="button"
                onClick={clearError}
                className="text-red-300/70 hover:text-red-200 shrink-0"
                aria-label="Dismiss error"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Grade summary card */}
        <div className={`${gradeCardColor} text-white rounded-2xl p-5 mb-6 shadow-xl`} data-testid="grade-summary-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider opacity-80">Overall Grade</div>
              <div className="text-4xl font-bold" data-testid="grade-value">
                {computed.overall_grade || previewScore.grade}
              </div>
              <div className="text-sm opacity-90 mt-1">
                {form.results && Object.keys(form.results).length > 0
                  ? 'Auto-computed from checkpoint answers'
                  : 'Fill checkpoints below to compute score'}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider opacity-80">Score</div>
              <div className="text-4xl font-bold" data-testid="score-value">
                {(computed.overall_score ?? previewScore.overall).toFixed(1)}
                <span className="text-lg opacity-70">/100</span>
              </div>
              {form.booking_id && (
                <div className="text-xs opacity-80 mt-1 font-mono">{form.booking_id}</div>
              )}
            </div>
          </div>
        </div>

        {/* Public sharing */}
        {savedId && (
          <section className={`mb-6 rounded-xl border p-4 transition ${
            share.is_public
              ? 'bg-teal-500/10 border-teal-500/40'
              : 'bg-white/5 border-white/10'
          }`} data-testid="share-section">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  share.is_public ? 'bg-teal-500/20 text-teal-300' : 'bg-white/5 text-slate-400'
                }`}>
                  {share.is_public ? <Globe size={16} /> : <Lock size={16} />}
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-slate-100">
                    {share.is_public ? 'Public report link is ON' : 'Public report link is OFF'}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {share.is_public
                      ? 'Anyone with the link can view a sanitized summary and download the branded PDF.'
                      : 'Turn on to generate a shareable read-only PDF link. Owner phone, chassis and engine numbers are always redacted.'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={toggleShare}
                disabled={shareBusy}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition disabled:opacity-60 ${
                  share.is_public
                    ? 'bg-white/10 hover:bg-white/15 text-slate-200 border border-white/20'
                    : 'bg-teal-500 hover:bg-teal-400 text-slate-950'
                }`}
                data-testid="toggle-share-btn"
              >
                {shareBusy ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
                {share.is_public ? 'Disable link' : 'Enable public link'}
              </button>
            </div>
            {share.is_public && publicUrl && (
              <div className="mt-3 flex items-center gap-2 bg-slate-900/50 border border-teal-500/30 rounded-lg px-2 py-1.5">
                <input
                  readOnly
                  value={publicUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 bg-transparent text-xs text-teal-300 font-mono focus:outline-none"
                  data-testid="share-url-input"
                />
                <button
                  type="button"
                  onClick={copyPublicUrl}
                  className="inline-flex items-center gap-1 text-[11px] bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/40 text-teal-200 px-2 py-1 rounded"
                  data-testid="copy-share-url"
                >
                  <Copy size={11} /> {copied ? 'Copied!' : 'Copy'}
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] bg-white/10 hover:bg-white/15 border border-white/20 text-slate-200 px-2 py-1 rounded"
                  data-testid="open-share-url"
                >
                  Open
                </a>
              </div>
            )}
          </section>
        )}

        {/* Vehicle & inspection meta */}
        <section className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <h2 className="text-sm font-semibold text-teal-300 uppercase tracking-wider mb-3">Vehicle & Inspection</h2>
          {(() => {
            const makes = Object.keys(vehicleCatalog.cars || {}).sort();
            const modelsForMake = vehicleCatalog.cars?.[form.make] || [];
            const variantsForModel = (vehicleCatalog.car_variants?.[form.make] || {})[form.model] || [];
            const generic = vehicleCatalog._generic_variants || [];
            // Merge specific + generic (specific first, de-duped)
            const variantSuggestions = Array.from(new Set([...variantsForModel, ...generic]));
            return (
              <>
                <datalist id="ucinsp-makes">
                  {makes.map((m) => <option key={m} value={m} />)}
                </datalist>
                <datalist id="ucinsp-models">
                  {modelsForMake.map((m) => <option key={m} value={m} />)}
                </datalist>
                <datalist id="ucinsp-variants">
                  {variantSuggestions.map((v) => <option key={v} value={v} />)}
                </datalist>
              </>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            {/* Cascading Make / Model / Variant — data from CarWale + CarDekho */}
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Make</div>
              <input
                list="ucinsp-makes"
                value={form.make || ''}
                onChange={(e) => {
                  const nextMake = e.target.value;
                  setForm((f) => {
                    // If the make changes, clear model/variant only if they no
                    // longer belong to the new make's catalog — preserves user
                    // input when they're just refining the string.
                    const newModels = vehicleCatalog.cars?.[nextMake] || [];
                    const modelStillValid = !f.model || newModels.length === 0 || newModels.includes(f.model);
                    return {
                      ...f,
                      make: nextMake,
                      model: modelStillValid ? f.model : '',
                      variant: modelStillValid ? f.variant : '',
                    };
                  });
                }}
                placeholder="Toyota, Maruti Suzuki, Honda…"
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                data-testid="vehicle-field-make"
              />
            </label>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Model</div>
              <input
                list="ucinsp-models"
                value={form.model || ''}
                onChange={(e) => {
                  const nextModel = e.target.value;
                  setForm((f) => {
                    const variantsForNew = (vehicleCatalog.car_variants?.[f.make] || {})[nextModel] || [];
                    const variantStillValid = !f.variant || variantsForNew.length === 0 || variantsForNew.includes(f.variant);
                    return {
                      ...f,
                      model: nextModel,
                      variant: variantStillValid ? f.variant : '',
                    };
                  });
                }}
                placeholder="Innova Crysta, Swift, City…"
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                data-testid="vehicle-field-model"
              />
            </label>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Variant</div>
              <input
                list="ucinsp-variants"
                value={form.variant || ''}
                onChange={(e) => setField('variant', e.target.value)}
                placeholder="ZX / VXi / SX(O) — or type your own"
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                data-testid="vehicle-field-variant"
              />
            </label>
            {[
              ['manufacture_year', 'Year'], ['km_driven', 'KM Driven'],
              ['fuel_type', 'Fuel Type'], ['transmission', 'Transmission'], ['color', 'Color'],
              ['registration_number', 'Reg. Number'], ['chassis_number', 'Chassis No.'], ['engine_number', 'Engine No.'],
              ['rto_authority', 'RTO Authority'], ['registration_date', 'Reg. Date'], ['registration_expiry', 'Reg. Expiry'],
              ['engine_cc', 'Engine (cc)'], ['seat_capacity', 'Seats'], ['emission_norms', 'Emission Norms'],
              ['body_type', 'Body Type'], ['insurance_valid_until', 'Insurance Until'], ['puc_valid_until', 'PUC Until'],
              ['owner_name', 'Owner Name'], ['owner_phone', 'Owner Phone'],
              ['ownership_type', 'Ownership'], ['owner_count', 'Owner Count'],
              ['booking_id', 'Booking ID (auto)'], ['inspection_date', 'Inspection Date'],
              ['inspection_location', 'Location'], ['inspector_name', 'Inspector Name'],
            ].map(([k, label]) => (
              <label key={k} className="block">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
                <input
                  type={['manufacture_year', 'km_driven', 'engine_cc', 'seat_capacity', 'owner_count'].includes(k) ? 'number'
                    : k.includes('date') || k.includes('valid_until') || k === 'registration_expiry' ? 'date' : 'text'}
                  value={form[k] ?? ''}
                  onChange={(e) => setField(k, e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                  data-testid={`vehicle-field-${k}`}
                />
              </label>
            ))}
          </div>
        </section>

        {/* Sections */}
        {schema.map((section) => {
          const isOpen = expandedSection === section.slug;
          const secScore = computed.section_scores?.[section.slug] ?? previewScore.sections[section.slug];
          return (
            <section key={section.slug} className="bg-white/5 border border-white/10 rounded-xl mb-4 overflow-hidden" data-testid={`section-${section.slug}`}>
              <button
                type="button"
                onClick={() => setExpandedSection(isOpen ? null : section.slug)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition"
                data-testid={`section-header-${section.slug}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-teal-300">{section.title}</span>
                  <span className="text-[11px] text-slate-500">{section.items.length} checks</span>
                </div>
                <div className="flex items-center gap-3">
                  {typeof secScore === 'number' && (
                    <span className="text-xs font-semibold text-slate-200" data-testid={`section-score-${section.slug}`}>
                      {secScore.toFixed(0)} / 100
                    </span>
                  )}
                  <span className="text-slate-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-3">
                  {section.note && (
                    <p className="text-xs text-slate-500 mb-2 border-l-2 border-teal-500/50 pl-2">{section.note}</p>
                  )}
                  {section.items.map((item) => (
                    <ParamRow
                      key={item.key}
                      item={item}
                      result={form.results?.[item.key]}
                      onChange={updateResult}
                      onUploadPhoto={onUploadPhoto}
                      onRemovePhoto={onRemovePhoto}
                      uploadingKey={uploadingKey}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {/* Repair estimates */}
        <div className="mb-6">
          <RepairEstimateEditor
            items={form.repair_estimates}
            onChange={(v) => setField('repair_estimates', v)}
          />
        </div>

        {/* Valuation */}
        <section className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-teal-300 uppercase tracking-wider">Estimated Fair Market Value</h2>
            <button
              type="button"
              onClick={fetchMarketValuation}
              disabled={valuationBusy || !form.make || !form.model}
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-500 hover:bg-teal-400 text-slate-950 px-3 py-1.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="fetch-market-valuation-btn"
              title={!form.make || !form.model ? 'Fill Make and Model first' : 'Fetch live comparable listings from Cars24, Spinny & Cartrade'}
            >
              {valuationBusy
                ? <><Loader2 size={12} className="animate-spin" /> Fetching…</>
                : <><TrendingUp size={12} /> Fetch from Cars24, Spinny &amp; Cartrade</>}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <label>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Min (₹)</div>
              <input
                type="number"
                value={form.valuation_min}
                onChange={(e) => setField('valuation_min', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                data-testid="valuation-min"
              />
            </label>
            <label>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Max (₹)</div>
              <input
                type="number"
                value={form.valuation_max}
                onChange={(e) => setField('valuation_max', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                data-testid="valuation-max"
              />
            </label>
            <label className="sm:col-span-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Notes</div>
              <textarea
                rows={2}
                value={form.valuation_notes}
                onChange={(e) => setField('valuation_notes', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                data-testid="valuation-notes"
              />
            </label>
          </div>
          {valuationResult && (
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <p className="text-xs text-slate-400" data-testid="valuation-summary">
                  {valuationResult.message}
                </p>
                {valuationResult.valuation_median && (
                  <span className="text-xs text-teal-300">
                    Median: <b>₹ {valuationResult.valuation_median.toLocaleString('en-IN')}</b>
                  </span>
                )}
              </div>
              {/* Fallback buttons — always show so inspector can drill deeper */}
              <div className="flex flex-wrap gap-2">
                {valuationResult.fallback_urls?.cars24 && (
                  <a
                    href={valuationResult.fallback_urls.cars24}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 px-2 py-1 rounded"
                    data-testid="fallback-cars24"
                  >
                    <ExternalLink size={11} /> Cars24 search
                  </a>
                )}
                {valuationResult.fallback_urls?.spinny && (
                  <a
                    href={valuationResult.fallback_urls.spinny}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 px-2 py-1 rounded"
                    data-testid="fallback-spinny"
                  >
                    <ExternalLink size={11} /> Spinny search
                  </a>
                )}
                {valuationResult.fallback_urls?.cartrade && (
                  <a
                    href={valuationResult.fallback_urls.cartrade}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 px-2 py-1 rounded"
                    data-testid="fallback-cartrade"
                  >
                    <ExternalLink size={11} /> Cartrade search
                  </a>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Comparable listings */}
        {valuationResult?.comparables?.length > 0 && (
          <section className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6" data-testid="comparables-section">
            <h2 className="text-sm font-semibold text-teal-300 uppercase tracking-wider mb-3 flex items-center gap-2">
              <TrendingUp size={14} />
              Comparable Listings ({valuationResult.comparables.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {valuationResult.comparables.map((c, i) => (
                <a
                  key={`${c.source}-${i}`}
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group bg-slate-900/40 hover:bg-slate-900/70 border border-white/10 hover:border-teal-500/40 rounded-lg overflow-hidden transition"
                  data-testid={`comparable-${c.source}-${i}`}
                >
                  {c.thumb ? (
                    <img src={c.thumb} alt="" className="w-full h-32 object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-32 bg-slate-800 flex items-center justify-center text-slate-500 text-xs">
                      No image
                    </div>
                  )}
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        c.source === 'cars24'
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                          : c.source === 'spinny'
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                          : 'bg-sky-500/20 text-sky-300 border border-sky-500/30'
                      }`}>
                        {c.source}
                      </span>
                      <span className="text-sm font-semibold text-teal-300">
                        ₹ {(c.price / 100000).toFixed(2)}L
                      </span>
                    </div>
                    <p className="text-sm text-slate-200 line-clamp-2 group-hover:text-white transition">
                      {c.title}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
                      {c.year && <span>{c.year}</span>}
                      {c.km != null && <span>· {c.km.toLocaleString('en-IN')} km</span>}
                      {c.fuel && <span>· {c.fuel}</span>}
                    </div>
                  </div>
                </a>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-3">
              Prices are live from Cars24, Spinny &amp; Cartrade public search pages. Inventory changes daily; verify before quoting.
            </p>
          </section>
        )}

        {/* Outstanding traffic fines / e-challans (manual) */}
        <section className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <h2 className="text-sm font-semibold text-amber-300 uppercase tracking-wider mb-2">
            Outstanding Traffic Fines / e-Challans
          </h2>
          <p className="text-[11px] text-slate-500 mb-2">
            No free public API exists for Indian e-challans. Log observations from the
            customer&apos;s mParivahan / RTO records here — will be printed in the PDF.
          </p>
          <textarea
            rows={3}
            value={form.outstanding_fines_notes}
            onChange={(e) => setField('outstanding_fines_notes', e.target.value)}
            placeholder="e.g. 2 pending challans (Rs 1,500) — verified on mParivahan on 12-Feb-2026"
            className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-amber-400"
            data-testid="outstanding-fines-notes"
          />
        </section>

        {/* Surepass — RC verification + service history */}
        <section className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6" data-testid="surepass-section">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold text-teal-300 uppercase tracking-wider">
                Vehicle Service History (Surepass · VAHAN)
              </h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Fetches VAHAN RC verification + dealer service records via Surepass.
                Requires the vehicle to be saved with a registration number.
              </p>
            </div>
            <button
              type="button"
              onClick={fetchSurepass}
              disabled={surepassBusy || !savedId || !form.registration_number}
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-500 hover:bg-teal-400 text-slate-950 px-3 py-1.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="fetch-surepass-btn"
              title={
                !savedId
                  ? 'Save the inspection first'
                  : !form.registration_number
                  ? 'Fill registration number first'
                  : 'Fetch RC + service history from Surepass'
              }
            >
              {surepassBusy
                ? <><Loader2 size={12} className="animate-spin" /> Fetching…</>
                : <><TrendingUp size={12} /> Fetch RC + Service History</>}
            </button>
          </div>

          {surepassError && (
            <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2 mb-3" data-testid="surepass-error">
              {surepassError}
            </div>
          )}

          {form.rc_verification && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 text-xs mb-3" data-testid="surepass-rc-verification">
              {[
                ['Owner', form.rc_verification.owner_name],
                ['Maker / Model', [form.rc_verification.maker_description, form.rc_verification.maker_model].filter(Boolean).join(' ')],
                ['Fuel', form.rc_verification.fuel_type],
                ['Reg. Date', form.rc_verification.registration_date],
                ['Fitness Upto', form.rc_verification.fitness_upto],
                ['Insurance', [form.rc_verification.insurance_company, form.rc_verification.insurance_upto].filter(Boolean).join(' · ')],
                ['PUC Upto', form.rc_verification.puc_upto],
                ['RTO', form.rc_verification.rto],
                ['RC Status', form.rc_verification.rc_status],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 border-b border-white/5 py-1">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-slate-200 text-right">{v}</span>
                </div>
              ))}
            </div>
          )}

          {form.service_history_records?.length > 0 ? (
            <div className="overflow-x-auto" data-testid="surepass-service-records">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-white/10">
                    <th className="text-left py-1.5 pr-2">Date</th>
                    <th className="text-left py-1.5 px-2">Dealer</th>
                    <th className="text-right py-1.5 px-2">Odometer</th>
                    <th className="text-left py-1.5 px-2">Service Type</th>
                    <th className="text-right py-1.5 pl-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {form.service_history_records.map((r, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-1.5 pr-2 text-slate-300">{r.service_date || '—'}</td>
                      <td className="py-1.5 px-2 text-slate-300">
                        {r.dealer_name || '—'}
                        {r.dealer_location && <span className="text-slate-500"> · {r.dealer_location}</span>}
                      </td>
                      <td className="py-1.5 px-2 text-right text-slate-300">
                        {r.odometer != null ? `${r.odometer.toLocaleString('en-IN')} km` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-slate-300">{r.service_type || '—'}</td>
                      <td className="py-1.5 pl-2 text-right text-teal-300 font-semibold">
                        {r.invoice_amount != null ? `₹ ${r.invoice_amount.toLocaleString('en-IN')}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : form.rc_verification ? (
            <p className="text-[11px] text-slate-500">
              No dealer service records returned. The vehicle may have been serviced only at private garages.
            </p>
          ) : (
            <p className="text-[11px] text-slate-500">
              Records will appear here after fetching. Not fetched yet.
            </p>
          )}
        </section>

        {/* Overall notes */}
        <section className="bg-white/5 border border-white/10 rounded-xl p-4 mb-8">
          <h2 className="text-sm font-semibold text-teal-300 uppercase tracking-wider mb-3">Inspector Notes</h2>
          <textarea
            rows={3}
            value={form.overall_notes}
            onChange={(e) => setField('overall_notes', e.target.value)}
            placeholder="Optional summary observations for the buyer…"
            className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-teal-400"
            data-testid="overall-notes"
          />
        </section>

        {/* Bottom save bar */}
        <div className="flex justify-end gap-2 pb-8">
          <button
            onClick={() => onSave()}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {savedId ? 'Save changes' : 'Save inspection'}
          </button>
          <button
            onClick={() => onSave({ downloadAfter: true })}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-sm px-4 py-2 rounded-lg disabled:opacity-60"
          >
            <FileDown size={14} /> Save + PDF
          </button>
        </div>
      </main>
    </div>
  );
};

export default UsedCarInspectionForm;
